import {
  CalendarDays,
  CircleHelp,
  ClipboardList,
  MapPin,
  PencilLine,
  Plus,
  Star,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/table";
import { OpportunityHierarchyItem, ProgramHierarchyItem } from "../types";
import {
  OPPORTUNTY_TYPE,
  OPPORTUNITY_TYPE_LABELS,
} from "../types/campaign-status.constants";
import { formatDate } from "./campaign.utils";
import {
  OpportunityEditor,
  type OpportunityFormValues,
} from "./program-period-section/OpportunityEditor";

interface OpportunityTypeCard {
  type: OPPORTUNTY_TYPE;
  title: string;
  description: string;
  icon: LucideIcon;
  capabilities: Array<{
    label: string;
    icon: LucideIcon;
  }>;
}

const opportunityTypeCards: OpportunityTypeCard[] = [
  {
    type: OPPORTUNTY_TYPE.Event,
    title: "Event",
    description: "Link an existing in-person or online event to this program.",
    icon: CalendarDays,
    capabilities: [
      { label: "Date & time", icon: CalendarDays },
      { label: "Location", icon: MapPin },
      { label: "Points", icon: Star },
    ],
  },
  {
    type: OPPORTUNTY_TYPE.Survey,
    title: "Survey",
    description: "Link an existing survey to collect feedback from residents.",
    icon: ClipboardList,
    capabilities: [
      { label: "Questions", icon: CircleHelp },
      { label: "Target audience", icon: Users },
      { label: "Points", icon: Star },
    ],
  },
];

interface OpportunitySectionProps {
  saving: boolean;
  selectedProgram: ProgramHierarchyItem | null;
  expandedOpportunityId: string | null;
  onToggleOpportunityDetails: (opportunity: OpportunityHierarchyItem) => void;
  onCreateOpportunity: (values: OpportunityFormValues) => void | Promise<void>;
  onUpdateOpportunity: (
    opportunityId: string,
    values: OpportunityFormValues,
  ) => void | Promise<void>;
}

export function OpportunitySection({
  saving,
  selectedProgram,
  expandedOpportunityId,
  onToggleOpportunityDetails,
  onCreateOpportunity,
  onUpdateOpportunity,
}: OpportunitySectionProps) {
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<OPPORTUNTY_TYPE>(
    OPPORTUNTY_TYPE.Event,
  );

  useEffect(() => {
    if (!selectedProgram) {
      setSelectedOpportunityId(null);
      setIsCreateOpen(false);
      return;
    }

    if (
      selectedOpportunityId &&
      !selectedProgram.opportunities.some(
        (opportunity) => opportunity.id === selectedOpportunityId,
      )
    ) {
      setSelectedOpportunityId(null);
    }
  }, [selectedOpportunityId, selectedProgram]);

  const closeCreateFlow = () => {
    setIsCreateOpen(false);
    setSelectedType(OPPORTUNTY_TYPE.Event);
  };

  const openOpportunity = (opportunity: OpportunityHierarchyItem) => {
    setSelectedOpportunityId(opportunity.id);
    if (expandedOpportunityId !== opportunity.id) {
      onToggleOpportunityDetails(opportunity);
    }
  };

  const submitCreateOpportunity = async (values: OpportunityFormValues) => {
    await onCreateOpportunity(values);
    closeCreateFlow();
  };

  return (
    <Card className="overflow-hidden border-[#DDEBE3] shadow-[0px_10px_24px_rgba(0,66,50,0.05)]">
      <div className="border-b border-[#E6EFE9] px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-[#004232]">
              {selectedProgram
                ? `Opportunities: ${selectedProgram.name}`
                : "Opportunities"}
            </h2>
            <p className="mt-1 text-sm text-[#6A7282]">
              {selectedProgram
                ? "Add an Event or Survey source, then select a row to review or update its association."
                : "Select a program row above to open its opportunity workspace."}
            </p>
          </div>

          <Button
            variant="primary"
            type="button"
            size="sm"
            disabled={!selectedProgram || saving}
            onClick={() => setIsCreateOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Create
          </Button>
        </div>
      </div>

      {!selectedProgram ? (
        <div className="px-6 py-5">
          <div className="rounded-[18px] border border-[#DDEBE3] bg-[#F8FBF9] p-5">
            <div className="text-sm font-semibold text-[#004232]">
              Choose a program first
            </div>
            <p className="mt-2 text-sm text-[#6A7282]">
              The opportunity builder and table become available after a program row is selected.
            </p>
          </div>
        </div>
      ) : (
        <>
          {isCreateOpen ? (
            <section className="mx-6 mt-5 rounded-2xl border border-[#DDEBE3] bg-[#FCFDFC] p-5 shadow-[0px_10px_24px_rgba(0,66,50,0.04)]">
              <div className="flex items-start gap-3 border-b border-[#E6EFE9] pb-4">
                <div className="rounded-xl bg-[#E8FFF0] p-2.5 text-[#00A86B]">
                  <Plus className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#004232]">Create Opportunity</h3>
                  <p className="mt-1 text-sm text-[#6A7282]">
                    Choose an opportunity type, then select an existing source below.
                  </p>
                </div>
              </div>

              <div
                role="radiogroup"
                aria-label="Opportunity type"
                className="mt-5 grid gap-3 md:grid-cols-2"
              >
                {opportunityTypeCards.map((typeCard) => {
                  const isSelected = selectedType === typeCard.type;
                  const Icon = typeCard.icon;

                  return (
                    <button
                      key={typeCard.type}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => setSelectedType(typeCard.type)}
                      className={`relative rounded-xl border p-5 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A86B] focus-visible:ring-offset-2 ${
                        isSelected
                          ? "border-[#00C98D] bg-[#F5FFF9] shadow-[0px_8px_20px_rgba(0,168,107,0.08)]"
                          : "border-[#DDEBE3] bg-white hover:border-[#9DDDC4] hover:bg-[#FAFFFC]"
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`absolute right-4 top-4 flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                          isSelected
                            ? "border-[#00C98D] bg-[#00C98D] text-white"
                            : "border-[#C8D4E3] bg-white"
                        }`}
                      >
                        {isSelected ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
                      </span>
                      <span className={`mb-8 flex h-12 w-12 items-center justify-center rounded-2xl ${
                        isSelected ? "bg-[#E8FFF0] text-[#00A86B]" : "bg-[#F4F7FB] text-[#57708E]"
                      }`}>
                        <Icon className="h-6 w-6" />
                      </span>
                      <span className="block text-lg font-semibold text-[#0F172A]">{typeCard.title}</span>
                      <span className="mt-1 block text-sm leading-5 text-[#6A7282]">
                        {typeCard.description}
                      </span>
                      <span className="mt-4 flex flex-wrap gap-2">
                        {typeCard.capabilities.map((capability) => {
                          const CapabilityIcon = capability.icon;
                          return (
                            <span
                              key={capability.label}
                              className="inline-flex items-center gap-1.5 rounded-md border border-[#DDE5EF] bg-white px-2.5 py-1.5 text-xs font-medium text-[#52606D]"
                            >
                              <CapabilityIcon className="h-3.5 w-3.5" />
                              {capability.label}
                            </span>
                          );
                        })}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 border-t border-[#E6EFE9] pt-5">
                <OpportunityEditor
                  selectedProgram={selectedProgram}
                  selectedOpportunity={null}
                  saving={saving}
                  initialType={selectedType}
                  lockType
                  compact
                  onCancel={closeCreateFlow}
                  onSubmit={submitCreateOpportunity}
                />
              </div>
            </section>
          ) : null}

          <Table containerClassName="mt-5 rounded-b-[20px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[28%]">Opportunity Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Neighborhood</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead className="w-20 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selectedProgram.opportunities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-sm text-[#6A7282]">
                    No opportunities yet for this program.
                  </TableCell>
                </TableRow>
              ) : (
                selectedProgram.opportunities.map((opportunity) => {
                  const isExpanded =
                    expandedOpportunityId === opportunity.id || selectedOpportunityId === opportunity.id;

                  return (
                    <Fragment key={opportunity.id}>
                      <TableRow
                        onClick={() => openOpportunity(opportunity)}
                        className={`cursor-pointer ${isExpanded ? "bg-[#F8FFFA]" : ""}`}
                      >
                        <TableCell className="font-medium text-[#0F172A]">
                          {opportunity.name}
                        </TableCell>
                        <TableCell className="text-sm text-[#52606D]">
                          {OPPORTUNITY_TYPE_LABELS[opportunity.type ?? OPPORTUNTY_TYPE.Event]}
                        </TableCell>
                        <TableCell className="text-sm text-[#52606D]">
                          {opportunity.neighborhoodName ?? "No neighborhood assigned"}
                        </TableCell>
                        <TableCell className="text-sm text-[#52606D]">
                          {opportunity.startDate ? formatDate(opportunity.startDate) : "—"}
                        </TableCell>
                        <TableCell className="text-sm text-[#52606D]">
                          {opportunity.endDate ? formatDate(opportunity.endDate) : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <button
                            type="button"
                            aria-label={`Edit ${opportunity.name}`}
                            title="Edit opportunity"
                            onClick={(event) => {
                              event.stopPropagation();
                              openOpportunity(opportunity);
                            }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#DDEBE3] text-[#004232] transition hover:bg-[#F3FBF6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A86B]"
                          >
                            <PencilLine className="h-4 w-4" />
                          </button>
                        </TableCell>
                      </TableRow>

                      {isExpanded ? (
                        <TableRow className="bg-[#FCFDFC] hover:bg-[#FCFDFC]">
                          <TableCell colSpan={6} className="p-4">
                            <OpportunityEditor
                              selectedProgram={selectedProgram}
                              selectedOpportunity={opportunity}
                              saving={saving}
                              onSubmit={(values) => onUpdateOpportunity(opportunity.id, values)}
                            />
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </>
      )}
    </Card>
  );
}
